import { EventEmitter, Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

export class HttpRequestOptions {
  id?: string | number;
  headers?: HttpHeaders;
  reportProgress?: boolean;
  params?: HttpParams;
  responseType?: any;
  withCredentials?: boolean;
  constructor(data?: any) {
    Object.assign(this, data);
  }
}

export enum ServiceErrorCode {
  ValidationError = 'VALIDATION_ERROR',
  Unauthorized = 'UNAUTHORIZED',
  ServiceUnavailable = 'SERVICE_UNAVAILABLE',
  Unexpected = 'UNEXPECTED_SERVER_ERROR',
  Forbidden = 'FORBIDDEN',
  NotFound = 'NOT_FOUND',
}

export class ServiceError {
  code: ServiceErrorCode;
  message?: string;
  data?: any;
}

const ERROR_CODE_TO_MESSAGE: Map<number, string> = new Map<number, string>([
  [400, ServiceErrorCode.ValidationError],
  [401, ServiceErrorCode.Unauthorized],
  [403, ServiceErrorCode.Forbidden],
  [404, ServiceErrorCode.NotFound],
  [500, ServiceErrorCode.ServiceUnavailable],
  [503, ServiceErrorCode.ServiceUnavailable],
]);

declare let swal: any;
@Injectable({ providedIn: 'root' })
export class ApiService {
  static onUnauthorized: EventEmitter<String> = new EventEmitter<String>();

  private baseUrl: string;

  constructor(public http: HttpClient) {
    this.baseUrl = 'api';
  }

  constructApiUrl(url: string) {
    return this.baseUrl + url;
  }

  get<T>(url: string, data?: any): Observable<T> {
    let params = new HttpParams();

    if (data) {
      for (const key of Object.keys(data)) {
        params = params.append(key, data[key]);
      }
    }
    return this.http
      .get<T>(this.constructApiUrl(url), this.defaultHttpOptions(params))
      .pipe(catchError(this.handleError));
  }

  post(url: string, data: any): Observable<any> {
    return this.http
      .post(this.constructApiUrl(url), data, this.defaultHttpOptions())
      .pipe(catchError(this.handleError));
  }

  put<T>(url: string, data: any, params?: any): Observable<T> {
    return this.http
      .put<T>(this.constructApiUrl(url), data, this.defaultHttpOptions(params))
      .pipe(catchError(this.handleError));
  }

  delete(url: string, params?: any): Observable<any> {
    const options: HttpRequestOptions = params
      ? this.defaultHttpOptions(params)
      : this.defaultHttpOptions();

    return this.http
      .delete(this.constructApiUrl(url), options)
      .pipe(catchError(this.handleError));
  }

  defaultHttpOptions(params?: HttpParams): HttpRequestOptions {
    const headers = new HttpHeaders();
    return new HttpRequestOptions({
      headers: headers,
      params: params,
    });
  }

  private handleError(error: any) {
    const body = JSON.parse(error._body);
    const errCode =
      ERROR_CODE_TO_MESSAGE.get(error.status) || ServiceErrorCode.Unexpected;
    if (body) {
      swal({
        title: body.message,
        type: 'warning',
      });
    }
    return Promise.reject({
      code: errCode,
      message: body ? body.message : null,
      data: body ? body.errors : null,
    } as ServiceError);
  }
}
